import { Hand } from 'app/design/icons/hand';
import { Label } from 'app/design/icons/label';
import { PinPointWithCheck } from 'app/design/icons/pinpoint';
import { SharingPlace } from 'app/design/icons/sharingPlace';
import { Column, Row } from 'app/design/primitives/box';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Text } from 'app/design/primitives/text';
import type { SharingPlaceDetails } from 'app/types/sharing-place';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import {
  IconArrowsExchange,
  IconArrowsSort,
  IconArrowsUpDown,
  IconHandClick,
  IconHandStop,
  IconInfoCircle,
  IconMapPinCheck,
  IconPackage,
  IconRosetteDiscountCheck,
  IconTag,
  IconUsersGroup,
} from '@tabler/icons-react-native';
const theme = createDSTheme();

const SHARING_PLACE_BENEFIT_ICON_MAP = {
  'arrows-up-down': IconArrowsUpDown,
  'arrows-exchange': IconArrowsExchange,
  'hand-click': IconHandClick,
  'hand-stop': IconHandStop,
  'map-pin-check': IconMapPinCheck,
  package: IconPackage,
  'rosette-discount-check': IconRosetteDiscountCheck,
  tag: IconTag,
  'users-group': IconUsersGroup,
} as const;

function SharingPlaceBenefitIcon({ icon }: { icon: string }) {
  const Icon =
    icon in SHARING_PLACE_BENEFIT_ICON_MAP
      ? SHARING_PLACE_BENEFIT_ICON_MAP[icon as keyof typeof SHARING_PLACE_BENEFIT_ICON_MAP]
      : IconInfoCircle;
  return <Icon strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['text.primary']} />;
}

function StrongText({ children }: { children: string }) {
  const segmentOccurrences = new Map<string, number>();

  return (
    <>
      {children.split(/(<strong>.*?<\/strong>)/gs).map((segment) => {
        const occurrence = segmentOccurrences.get(segment) ?? 0;
        segmentOccurrences.set(segment, occurrence + 1);
        const key = `${segment}-${String(occurrence)}`;
        const match = /^<strong>(.*?)<\/strong>$/s.exec(segment);
        return match ? (
          <Text key={key} fontWeight="700" variant="bodyLarge">
            {match[1]}
          </Text>
        ) : (
          <React.Fragment key={key}>{segment}</React.Fragment>
        );
      })}
    </>
  );
}

interface Properties {
  open: boolean;
  onClose: () => void;
  placeDetails: SharingPlaceDetails;
}

export function SharingPlaceHelpBottomSheet({ open, onClose, placeDetails }: Properties) {
  const { t } = useTranslation();
  const intro = placeDetails.sharingPlaceIntro;

  return (
    <BottomSheet open={open} onClose={onClose}>
      <ScrollView>
        <Column style={{ paddingBottom: theme.spacing['spacing.general.x12'] }}>
          {/* Banner */}
          <Column
            alignItems="center"
            justifyContent="center"
            style={{
              backgroundColor: theme.colors['surface.tertiary'],
              marginHorizontal: theme.spacing['spacing.general.x5'],
              borderRadius: theme.borderRadii['radius.lg'],
              height: 148,
              marginBottom: theme.spacing['spacing.general.x4'],
            }}
          >
            <SharingPlace width={80} height={80} />
          </Column>

          <Column style={{ paddingHorizontal: theme.spacing['spacing.general.x5'] }}>
            {intro ? (
              <>
                {intro.title ? (
                  <Text fontWeight="700" variant="headerXSmall" marginBottom="spacing.general.x2">
                    {intro.title}
                  </Text>
                ) : null}
                {intro.description ? (
                  <Text color="text.primary" variant="bodyLarge" marginBottom="spacing.general.x4">
                    <StrongText>{intro.description}</StrongText>
                  </Text>
                ) : null}
                {intro.benefits.map((benefit, index) => (
                  <React.Fragment key={`${benefit.icon}-${benefit.title ?? ''}-${benefit.subtitle ?? ''}`}>
                    <Row
                      alignItems="flex-start"
                      gap="spacing.general.x4"
                      style={{ paddingVertical: theme.spacing['spacing.general.x2'] }}
                    >
                      <Column
                        alignItems="center"
                        justifyContent="center"
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 28,
                          backgroundColor: theme.colors['surface.tertiary-neutral'],
                        }}
                      >
                        <SharingPlaceBenefitIcon icon={benefit.icon} />
                      </Column>
                      <Column flex={1} gap="spacing.general.x1">
                        {benefit.title ? (
                          <Text fontWeight="700" variant="bodyLarge">
                            {benefit.title}
                          </Text>
                        ) : null}
                        {benefit.subtitle ? (
                          <Text color="text.tertiary" variant="bodyBase">
                            {benefit.subtitle}
                          </Text>
                        ) : null}
                      </Column>
                    </Row>
                    {index < intro.benefits.length - 1 ? (
                      <View style={{ height: 1, backgroundColor: theme.colors['surface.disabled'] }} />
                    ) : null}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <>
                <Text fontWeight="700" variant="headerXSmall" marginBottom="spacing.general.x2">
                  {t('placeHelp.title', { ns: 'neighbour-profile' })}
                </Text>

                <Text variant="bodyBase" color="text.primary" marginBottom="spacing.general.x4">
                  {t('placeHelp.description.1', { ns: 'neighbour-profile' })}
                  <Text variant="bodyBase" fontWeight="700">
                    {t('placeHelp.description.bold1', { ns: 'neighbour-profile' })}
                  </Text>
                  {t('placeHelp.description.2', { ns: 'neighbour-profile' })}
                  <Text variant="bodyBase" fontWeight="700">
                    {t('placeHelp.description.bold2', { ns: 'neighbour-profile' })}
                  </Text>
                  {t('placeHelp.description.3', { ns: 'neighbour-profile' })}
                  <Text variant="bodyBase" fontWeight="700">
                    {t('placeHelp.description.bold3', { ns: 'neighbour-profile' })}
                  </Text>
                  {t('placeHelp.description.4', { ns: 'neighbour-profile' })}
                </Text>

                {[
                  {
                    key: 'borrow',
                    icon: <Hand color={theme.colors['text.primary']} width={22} height={22} />,
                    title: t('placeHelp.features.borrow.title', { ns: 'neighbour-profile' }),
                    description: t('placeHelp.features.borrow.description', { ns: 'neighbour-profile' }),
                  },
                  {
                    key: 'dropOff',
                    icon: (
                      <IconArrowsSort strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['text.primary']} />
                    ),
                    title: t('placeHelp.features.dropOff.title', { ns: 'neighbour-profile' }),
                    description: t('placeHelp.features.dropOff.description', { ns: 'neighbour-profile' }),
                  },
                  {
                    key: 'deals',
                    icon: <Label color={theme.colors['text.primary']} width={22} height={22} />,
                    title: t('placeHelp.features.deals.title', { ns: 'neighbour-profile' }),
                    description: t('placeHelp.features.deals.description', { ns: 'neighbour-profile' }),
                  },
                  {
                    key: 'frequenting',
                    icon: <PinPointWithCheck color={theme.colors['text.primary']} width={22} height={22} />,
                    title: t('placeHelp.features.frequenting.title', { ns: 'neighbour-profile' }),
                    description: t('placeHelp.features.frequenting.description', { ns: 'neighbour-profile' }),
                  },
                ].map((item, idx, arr) => (
                  <React.Fragment key={item.key}>
                    <Row
                      gap="spacing.general.x4"
                      alignItems="flex-start"
                      style={{ paddingVertical: theme.spacing['spacing.general.x3'] }}
                    >
                      <Column
                        alignItems="center"
                        justifyContent="center"
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 22,
                          backgroundColor: theme.colors['surface.tertiary-neutral'],
                        }}
                      >
                        {item.icon}
                      </Column>
                      <Column flex={1} gap="spacing.general.x1">
                        <Text fontWeight="700" variant="bodyBase">
                          {item.title}
                        </Text>
                        <Text color="text.tertiary" variant="bodyBase">
                          {item.description}
                        </Text>
                      </Column>
                    </Row>
                    {idx < arr.length - 1 ? (
                      <View style={{ height: 1, backgroundColor: theme.colors['surface.disabled'] }} />
                    ) : null}
                  </React.Fragment>
                ))}
              </>
            )}
          </Column>
        </Column>
      </ScrollView>
    </BottomSheet>
  );
}
